import {
  Line,
  Float32BufferAttribute,
  BufferAttribute,
  MathUtils,
  Vector3,
  Box3,
  BufferGeometry,
  Mesh,
  PlaneGeometry,
  MeshBasicMaterial,
} from "three";

/**
 * 本脚本用于框选物体
 * 先使用setModel()将模型加入数组
 * 再使用changeMode()打开框选
 */
class BoxSelection {
  constructor(viewer) {
    this.viewer = viewer;
    this.renderer = viewer.renderer;
    this.camera = viewer.activeCamera;
    this.scene = viewer.scene;
    this.useSelect = false; //这个值为true时才能框选物体

    this.selectionShape = null; //框选时在屏幕上出现的矩形区域

    this.startX = -Infinity;
    this.startY = -Infinity;
    this.startZ = 0;

    this.prevX = -Infinity;
    this.prevY = -Infinity;

    this.startPageX = 0;
    this.startPageY = 0;
    this.endPageX = 0;
    this.endPageY = 0;

    this.selectionPoints = [];
    this.rect = null;
    this.init();
  }

  /**
   * 切换相机 可以正交、透视相互切换，也可以同种类型的不同相机切换
   * @param {*} camera
   */
  setCamera(camera) {
    this.camera = camera;
    if (camera.isPerspectiveCamera) {
      this.camera.add(this.selectionShape);
      this.selectionShape.position.z = -1;
      this.camera.add(this.selectionPlane);
      this.selectionPlane.position.z = -1;
    } else {
      this.scene.add(this.selectionShape);
      this.selectionShape.position.set(0, 0, 0);
      this.selectionShape.rotation.set(0, 0, 0);
      this.selectionShape.scale.set(1, 1, 1);

      this.scene.add(this.selectionPlane);
      this.selectionPlane.position.set(0, 0, 0);
      this.selectionPlane.rotation.set(0, 0, 0);
      this.selectionPlane.scale.set(1, 1, 1);
    }
  }

  /**
   * 切换是否框选物体
   */
  changeMode(enabled) {
    if (this.useSelect === enabled) return;
    this.useSelect = enabled;
  }

  updatePlaneGeo() {}

  init() {
    // selection shape
    this.selectionShape = new Line();
    this.selectionShape.material.color.set(0x76bbff);
    this.selectionShape.renderOrder = 1;
    this.selectionShape.position.z = -1;
    this.selectionShape.material.depthTest = false;
    this.selectionShape.scale.setScalar(1);
    this.selectionShape.frustumCulled = false;
    this.camera.add(this.selectionShape);

    // 新增填充平面
    const planeGeometry = new PlaneGeometry(1, 1);
    const planeMaterial = new MeshBasicMaterial({
      color: 0x76bbff,
      transparent: true,
      opacity: 0.3,
      side: 2, // 双面绘制，确保平面背对着相机，让它显示在前面，不被遮挡
    });
    this.selectionPlane = new Mesh(planeGeometry, planeMaterial);
    this.selectionPlane.renderOrder = 1;
    this.selectionPlane.position.z = -1;
    this.selectionPlane.material.depthTest = false;
    this.selectionPlane.frustumCulled = false;
    this.selectionPlane.visible = false;
    this.camera.add(this.selectionPlane);

    //鼠标按下事件
    this.renderer.domElement.addEventListener("pointerdown", this.pointerDown);

    //鼠标松开事件
    this.renderer.domElement.addEventListener("pointerup", this.pointerUp);

    //鼠标移动事件
    this.renderer.domElement.addEventListener("pointermove", this.pointerMove);
  }

  clear() {
    if (this.selectionPlane) {
      this.selectionPlane.geometry.dispose();
      this.selectionPlane.material.dispose();
      this.camera.remove(this.selectionPlane);
    }

    this.selectionShape.geometry.dispose();
    this.selectionShape.material.dispose();
    this.selectionShape = null;
    //鼠标按下事件
    this.renderer.domElement.removeEventListener(
      "pointerdown",
      this.pointerDown
    );

    //鼠标松开事件
    this.renderer.domElement.removeEventListener("pointerup", this.pointerUp);

    //鼠标移动事件
    this.renderer.domElement.removeEventListener(
      "pointermove",
      this.pointerMove
    );

    this.viewer = null;
    this.renderer = null;
    this.camera = null;
    this.scene = null;
    this.useSelect = false;
  }

  /**
   *更新框选形状
   */
  update() {
    if (!this.selectionShape || !this.useSelect) return;

    //根据相机设置框选形状的位置，让它显示在屏幕上
    if (this.camera.isPerspectiveCamera) {
      this.selectionShape.geometry.setAttribute(
        "position",
        new Float32BufferAttribute(this.selectionPoints, 3, false)
      );

      const yScale =
        Math.tan((MathUtils.DEG2RAD * this.camera.fov) / 2) *
        this.selectionShape.position.z;
      this.selectionShape.scale.set(-yScale * this.camera.aspect, -yScale, 1);

      // 修正平面顶点数据（只需要4个顶点）
      const planePoints = this.selectionPoints.slice(0, 12); // 取前4个点
      this.selectionPlane.geometry.setAttribute(
        "position",
        new Float32BufferAttribute(planePoints, 3, false)
      );
      // 添加三角形索引（两个三角形组成矩形）
      const indices = new Uint16Array([0, 1, 2, 0, 2, 3]);
      this.selectionPlane.geometry.setIndex(new BufferAttribute(indices, 1));
      this.selectionPlane.scale.copy(this.selectionShape.scale);
    } else {
      const startX =
        ((this.startPageX - this.rect.left) / this.rect.width) * 2 - 1;
      const startY =
        (-(this.startPageY - this.rect.top) / this.rect.height) * 2 + 1;
      const endX = ((this.endPageX - this.rect.left) / this.rect.width) * 2 - 1;
      const endY =
        (-(this.endPageY - this.rect.top) / this.rect.height) * 2 + 1;

      this.camera.updateProjectionMatrix();
      let vec1 = new Vector3(startX, startY, -1).unproject(this.camera);
      let vec2 = new Vector3(endX, startY, -1).unproject(this.camera);
      let vec3 = new Vector3(endX, endY, -1).unproject(this.camera);
      let vec4 = new Vector3(startX, endY, -1).unproject(this.camera);
      let vec5 = vec1.clone();

      let dir = this.camera.getWorldDirection(new Vector3()).normalize();
      vec1.add(dir.clone().multiplyScalar(1.1));
      vec2.add(dir.clone().multiplyScalar(1.1));
      vec3.add(dir.clone().multiplyScalar(1.1));
      vec4.add(dir.clone().multiplyScalar(1.1));
      vec5.add(dir.clone().multiplyScalar(1.1));

      let points = [vec1, vec2, vec3, vec4, vec5];
      this.selectionShape.geometry.dispose();
      this.selectionShape.geometry = new BufferGeometry().setFromPoints(points);

      // 更新正交相机的填充平面
      this.selectionPlane.geometry.dispose();
      this.selectionPlane.geometry = new BufferGeometry().setFromPoints([
        vec1,
        vec2,
        vec3,
        vec4,
      ]);
      // 添加索引缓冲
      const indices = new Uint16Array([0, 1, 2, 0, 2, 3]);
      this.selectionPlane.geometry.setIndex(new BufferAttribute(indices, 1));
    }
  }

  pointerDown = (e) => {
    if (!this.useSelect) return;
    this.prevX = e.clientX;
    this.prevY = e.clientY;

    this.rect = this.renderer.domElement.getBoundingClientRect();
    this.startX = ((e.clientX - this.rect.left) / this.rect.width) * 2 - 1;
    this.startY = (-(e.clientY - this.rect.top) / this.rect.height) * 2 + 1;

    this.startPageX = e.clientX;
    this.startPageY = e.clientY;

    this.selectionPoints.length = [];

    this.startSelect = true;
  };

  pointerUp = (e) => {
    if (!this.useSelect) return;

    this.startSelect = false;

    this.shape();
    this.selectionShape.visible = false;
    this.selectionPlane.visible = false;
    this.selectionPoints.length = [];
  };

  pointerMove = (e) => {
    if (!this.useSelect || !this.startSelect) return;
    // If the left mouse button is not pressed
    if ((1 & e.buttons) === 0) {
      return;
    }

    const ex = e.clientX;
    const ey = e.clientY;

    let nx = ((e.clientX - this.rect.left) / this.rect.width) * 2 - 1; // (e.clientX / window.innerWidth) * 2 - 1;
    let ny = (-(e.clientY - this.rect.top) / this.rect.height) * 2 + 1; //- ((e.clientY / window.innerHeight) * 2 - 1);

    // set points for the corner of the box
    this.selectionPoints.length = 3 * 5;

    this.selectionPoints[0] = this.startX;
    this.selectionPoints[1] = this.startY;
    this.selectionPoints[2] = this.startZ;

    this.selectionPoints[3] = nx;
    this.selectionPoints[4] = this.startY;
    this.selectionPoints[5] = this.startZ;

    this.selectionPoints[6] = nx;
    this.selectionPoints[7] = ny;
    this.selectionPoints[8] = this.startZ;

    this.selectionPoints[9] = this.startX;
    this.selectionPoints[10] = ny;
    this.selectionPoints[11] = this.startZ;

    this.selectionPoints[12] = this.startX;
    this.selectionPoints[13] = this.startY;
    this.selectionPoints[14] = this.startZ;

    if (ex !== this.prevX || ey !== this.prevY) {
      this.endPageX = e.clientX;
      this.endPageY = e.clientY;
      this.update();
      if (!this.selectionShape.visible) this.selectionShape.visible = true;
      if (!this.selectionPlane.visible) this.selectionPlane.visible = true;
    }
    this.prevX = ex;
    this.prevY = ey;
  };

  setHighlightCB(cb) {
    this.highlightCB = cb;
  }

  /**
   * 松开鼠标后开始比较、隐藏mesh
   */
  shape() {
    if (!this.viewer) return;
    let highlightObj = [];
    if (this.selectionPoints.length < 12) return [];
    //根据框选矩形4个点和相机位置求出当前视景体的八个点，用视景体比较mesh是否在框选区域内
    let p1, p2, p3, p4;
    if (this.camera.isPerspectiveCamera) {
      this.selectionShape.updateMatrixWorld();
      p1 = new Vector3(
        this.selectionPoints[0],
        this.selectionPoints[1],
        this.selectionPoints[2]
      ).applyMatrix4(this.selectionShape.matrixWorld);
      p2 = new Vector3(
        this.selectionPoints[3],
        this.selectionPoints[4],
        this.selectionPoints[5]
      ).applyMatrix4(this.selectionShape.matrixWorld);
      p3 = new Vector3(
        this.selectionPoints[6],
        this.selectionPoints[7],
        this.selectionPoints[8]
      ).applyMatrix4(this.selectionShape.matrixWorld);
      p4 = new Vector3(
        this.selectionPoints[9],
        this.selectionPoints[10],
        this.selectionPoints[11]
      ).applyMatrix4(this.selectionShape.matrixWorld);
    } else {
      const position = this.selectionShape.geometry.getAttribute("position");
      p1 = new Vector3(position.getX(0), position.getY(0), position.getZ(0));
      p2 = new Vector3(position.getX(1), position.getY(1), position.getZ(1));
      p3 = new Vector3(position.getX(2), position.getY(2), position.getZ(2));
      p4 = new Vector3(position.getX(3), position.getY(3), position.getZ(3));
    }
    //求出4个点投影在相机上的位置  ，符合笛卡尔坐标系，范围[-1,1]
    p1 = p1.project(this.camera);
    p2 = p2.project(this.camera);
    p3 = p3.project(this.camera);
    p4 = p4.project(this.camera);

    let maxX = Math.max(p1.x, p2.x, p3.x, p4.x);
    let maxY = Math.max(p1.y, p2.y, p3.y, p4.y);
    let minX = Math.min(p1.x, p2.x, p3.x, p4.x);
    let minY = Math.min(p1.y, p2.y, p3.y, p4.y);

    //遍历所有模型的所有mesh
    for (var i = 0; i < this.viewer.singleModels.length; i++) {
      const model = this.viewer.singleModels[i];
      if (model._delete || !model.visible) continue; // 剔除被删除的物体
      const box = new Box3().expandByObject(model);

      //查看这个mesh的包围盒中心是否在框选区域内
      if (false) {
        let boxCenter = box.getCenter(new Vector3());
        //求出包围盒中心投影在相机上的位置
        let pro = boxCenter.project(this.camera);
        //判断这个点是否在4个点范围内
        if (pro.x > minX && pro.x < maxX && pro.y > minY && pro.y < maxY) {
          highlightObj.push(model);
        }
      }
      // 要求模型整个包围盒都在框选范围内
      else {
        // 计算模型包围盒在NDC空间中的投影范围
        let modelMinX = Infinity,
          modelMaxX = -Infinity;
        let modelMinY = Infinity,
          modelMaxY = -Infinity;

        // 获取包围盒8个顶点
        const temp = new Vector3();
        for (let x = 0; x <= 1; x++) {
          for (let y = 0; y <= 1; y++) {
            for (let z = 0; z <= 1; z++) {
              temp
                .set(
                  x ? box.max.x : box.min.x,
                  y ? box.max.y : box.min.y,
                  z ? box.max.z : box.min.z
                )
                .project(this.camera);

              modelMinX = Math.min(modelMinX, temp.x);
              modelMaxX = Math.max(modelMaxX, temp.x);
              modelMinY = Math.min(modelMinY, temp.y);
              modelMaxY = Math.max(modelMaxY, temp.y);
            }
          }
        }

        // 判断模型投影范围是否完全在框选范围内
        if (
          modelMinX > minX &&
          modelMaxX < maxX &&
          modelMinY > minY &&
          modelMaxY < maxY
        ) {
          highlightObj.push(model);
        }
      }
    }
    if (this.highlightCB) this.highlightCB(highlightObj);
  }
}
export { BoxSelection };
